// src/components/FeaturedCarousel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FeaturedBig from "./FeaturedBig.jsx";
import { API } from "../config/api.js";
import { get } from "../lib/http.js";
import { getEventScheduleParts, formatEventScheduleLabel, resolveEventPosterUrl } from "../api/events.js";

const AUTOPLAY_MS = 5000;

export default function FeaturedCarousel() {
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await get(`${API.events.getEvent}`, {
          headers: { "Content-Type": "application/json" },
        });
        const all = Array.isArray(res?.events) ? res.events : [];
        const verified = all.filter(
          (e) => e?.isVerified === true || e?.isVarifid === true
        );
        const list = verified.length ? verified : all.slice(0, 1);
        if (alive) setEvents(list);
      } catch (e) {
        console.error("FeaturedCarousel fetch error:", e);
      }
    })();
    return () => {
      alive = false;
      clearInterval(timerRef.current);
    };
  }, []);

  const slides = useMemo(
    () =>
      events.map((ev) => {
        const schedule = getEventScheduleParts(ev);

        return {
          id: ev?._id || ev?.id,
          title: ev?.name || ev?.title || "Event",
          subtitle: ev?.venue?.name
            ? `${ev.venue.name}${ev?.venue?.city ? `, ${ev.venue.city}` : ""}`
            : ev?.location || "",
          location: ev?.venue?.name
            ? `${ev.venue.name}${ev?.venue?.city ? `, ${ev.venue.city}` : ""}`
            : ev?.location || "",
          image: resolveEventPosterUrl(ev),
          category: ev?.category || ev?.type || "Featured",
          about: ev?.description || "",
          duration:
            typeof ev?.duration === "number"
              ? `${Math.floor(ev.duration / 60)}h ${ev.duration % 60}m`
              : ev?.duration || "",
          schedule,
          dateBlock:
            schedule.dates.length === 1
              ? { date: schedule.dates[0], time: schedule.time || "" }
              : schedule.dates.length > 1
              ? { date: schedule.dates.join(" & "), time: schedule.time || "" }
              : undefined,
          dateFull: schedule.full || formatEventScheduleLabel(ev),
          languages: ev?.language,
          age: ev?.ageRestriction ? `${ev.ageRestriction}+` : undefined,
        };
      }),
    [events]
  );

  useEffect(() => {
    if (!slides.length || paused || slides.length <= 1) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [slides.length, paused]);

  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (!slides.length) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center font-sans text-sm text-slate-500 shadow-sm">
        No premium events to show right now.
      </div>
    );
  }

  const onOpen = (id) => {
    setPaused(true);
    if (id) nav(`/events/${id}`);
  };

  return (
    <div
      className="group overflow-x-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative overflow-hidden rounded-2xl">
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{
            width: `${slides.length * 100}%`,
            transform: `translateX(-${active * (100 / slides.length)}%)`,
          }}
        >
          {slides.map((slide, idx) => (
            <div
              key={`premium-${slide.id ?? "event"}-${idx}`}
              className="w-full shrink-0 px-0"
              style={{ width: `${100 / slides.length}%` }}
              onClickCapture={() => setPaused(true)}
              onClick={() => onOpen(slide.id)}
            >
              <FeaturedBig item={slide} />
            </div>
          ))}
        </div>
      </div>
      {/* Dots intentionally removed */}
    </div>
  );
}
